import { cloneElement, useState, type ReactElement, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle2, ChevronDown, Sparkles } from 'lucide-react'
import { BackButton } from '@/components/back-button'
import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCustomerCommThread, useRecordCustomerComm } from '@/api/hooks'
import { ActionEmailContext } from '@/features/communications/action-email'
import { useApproveMessage, useRejectMessage, useSendMessage, useUpdateMessage } from '@/features/communications/hooks'
import { ThreadMailboxProvider } from '@/features/communications/mailbox'
import { POConversations } from '@/features/communications/po-conversations'
import { toConversationThread, toPurchaseOrderAction } from '@/features/customer-comms/thread-model'
import { ActionItemsList, CustomerActionCard } from '@/features/purchase-orders/actions'
import { ActionItemSourceProvider } from '@/features/purchase-orders/action-source'
import { ActionOptionContext, type ActionOptionRenderer } from '@/features/purchase-orders/action-option-context'
import { formatApiErrorMessage } from '@/lib/api-error'
import { useActorName } from '@/hooks/use-actor-name'
import { usePageTitle } from '@/lib/page-title'
import { cn, formatMoney } from '@/lib/utils'
import {
  canDecide,
  CHECK_LABELS,
  KIND_LABELS,
  REASON_LABELS,
  coverageText,
  STATUS,
  aiCall,
  estimatedTaxTotal,
  formatCalendarDate,
  formatDay,
  formatDayTime,
  useTaxExemption,
  useUpdateTaxExemption,
  type TaxExemptionDetail,
} from './api'
import { CertificatePreview } from './certificate-preview'
import { ApproveForm, RejectForm } from './decision-actions'

// Customer emails sign as the company (USA-1353), as on a customer thread's page.
const CUSTOMER_SIGNOFF = 'Thank you,\nUSA Restaurant Suppliers\nCustomer Service\nstatus@shopusars.com'
const CONVERSATION_ID = 'form-conversation'

type ComposeRequest = { id: number; text: string }

const AI_TONES = {
  approve: 'text-emerald-700 dark:text-emerald-300',
  decline: 'text-red-700 dark:text-red-300',
  unsure: 'text-amber-800 dark:text-amber-300',
  none: 'text-muted-foreground',
}

const SOURCE_LABELS = { account: 'on their account', checkout: 'at checkout' }

const DECIDED: Partial<Record<TaxExemptionDetail['status'], string>> = {
  approved: 'Approved',
  declined: 'Declined',
  revoked: 'Rejected',
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  )
}

function YesNo({ value, noTone }: { value: boolean; noTone: string }) {
  return value ? <span>Yes</span> : <span className={cn('font-medium', noTone)}>No</span>
}

// A value that edits in place, lined up with the plain values beside it.
const EDIT_VALUE =
  '-mx-2 -my-1 inline-flex min-h-8 max-w-full items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50'

/** An approved form's expiry, click to change (cleared: it doesn't expire); the customer is emailed. */
function ExpiryEdit({ form, display }: { form: TaxExemptionDetail; display: string }) {
  const update = useUpdateTaxExemption(form.uuid)
  return (
    <POInlineEditableField
      ariaLabel={`Expires: ${display}. Change the expiry date`}
      buttonClassName="-mx-2 -my-1 min-w-0"
      displayValue={display}
      kind="date"
      onConfirm={(raw) =>
        update.mutate(
          { coverage: form.coverage ?? 'all', exempt_categories: form.exempt_categories, expires_on: raw || null },
          {
            onSuccess: () => toast.success('Expiry saved. We emailed the customer.'),
            onError: (error) => toast.error(formatApiErrorMessage('Not saved', error)),
          },
        )
      }
      pending={update.isPending}
      showEditIcon
      title="Click to change the expiry date"
      value={form.expires_on ?? ''}
    />
  )
}

/** What the AI read off the form, beside the form itself, so staff can check one against the other. */
function WhatTheAiRead({ form }: { form: TaxExemptionDetail }) {
  const review = form.ai_review
  const kind = review?.document_kind ?? form.certificate_kind
  const states = review?.states ?? form.states ?? []
  // Staff may have corrected the expiry; theirs wins over what the AI read.
  const expires = formatCalendarDate(form.expires_on) ?? formatCalendarDate(review?.expires_on)
  const reason = form.exemption_reason ?? review?.exemption_reason ?? null
  // Staff's decision wins over the AI's reading once there is one.
  const coverage = form.coverage ?? review?.coverage ?? null
  const covers = coverageText(
    coverage,
    form.coverage ? form.exempt_categories : (review?.covered_categories ?? []),
  )
  // An approved form's expiry and coverage are corrected in place; the customer is emailed the change.
  const editable = form.status === 'approved'
  return (
    <div className="border-t p-4">
      <h2 className="mb-2 text-sm font-semibold">What the AI read</h2>
      {!review && form.status !== 'reviewing' ? (
        <p className="mb-2 text-sm text-amber-800 dark:text-amber-300">The AI could not read this form. Check it by eye.</p>
      ) : null}
      <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        <Fact label="Form">
          {kind ? KIND_LABELS[kind] : '—'}
          {review?.document_title ? <span className="block text-xs text-muted-foreground">{review.document_title}</span> : null}
        </Fact>
        <Fact label="Holder">{review?.holder_name || form.holder_name || '—'}</Fact>
        <Fact label="Number">
          <span className="font-mono text-xs">{review?.certificate_number || form.certificate_number || '—'}</span>
        </Fact>
        <Fact label="States">{states.length ? states.join(', ') : '—'}</Fact>
        {review ? (
          <>
            <Fact label="Signed">
              <YesNo noTone="text-red-700 dark:text-red-300" value={review.signed} />
            </Fact>
            <Fact label="Effective">{formatCalendarDate(review.effective_date) ?? '—'}</Fact>
          </>
        ) : null}
        <Fact label="Expires">
          {editable ? <ExpiryEdit display={expires ?? 'No end date'} form={form} /> : (expires ?? 'No end date')}
        </Fact>
        {reason ? <Fact label="Reason">{REASON_LABELS[reason]}</Fact> : null}
        {covers ? (
          <Fact label="Tax-free on">
            {editable ? (
              // Click the value to change what it covers: the same choice as approving it.
              <ApproveForm
                edit
                form={form}
                only="coverage"
                trigger={
                  <button aria-label={`Tax-free on: ${covers}. Change what it covers`} className={EDIT_VALUE} type="button">
                    <span className="min-w-0 text-left">{covers}</span>
                    <ChevronDown aria-hidden="true" className="size-3 shrink-0 opacity-60" />
                  </button>
                }
              />
            ) : (
              covers
            )}
            {/* Shopify exempts per state, never per item (USA-1578): say where and when it applies. */}
            {coverage === 'some' && states.length ? (
              <span className="block text-xs text-muted-foreground">
                Tax-free when the cart is only these items and ships to {states.join(', ')}. Mixed carts get
                the tax on these items refunded after the order.
              </span>
            ) : coverage !== 'some' && reason === 'resale' && states.length ? (
              <span className="block text-xs text-muted-foreground">Only on orders shipping to {states.join(', ')}</span>
            ) : null}
          </Fact>
        ) : null}
        <Fact label="Exempt use">
          Confirmed by the customer {formatDayTime(form.attested_at)} {SOURCE_LABELS[form.source]}
        </Fact>
      </dl>
    </div>
  )
}

function Checks({ form }: { form: TaxExemptionDetail }) {
  if (!form.checks.length) return null
  return (
    <div className="border-t p-4">
      <h2 className="mb-2 text-sm font-semibold">Checks</h2>
      <ul className="grid gap-1.5 text-sm">
        {form.checks.map((check) => (
          <li className="flex items-start gap-2" key={check.code}>
            {check.ok ? (
              <CheckCircle2 aria-label="Passed" className="mt-0.5 size-4 shrink-0 text-emerald-600" />
            ) : (
              <AlertTriangle aria-label="Look at this" className="mt-0.5 size-4 shrink-0 text-amber-600" />
            )}
            <span className="min-w-0 break-words">{check.message || CHECK_LABELS[check.code]}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Who decided, and when: the form's state in one line. */
function Decided({ form }: { form: TaxExemptionDetail }) {
  const actorName = useActorName()
  const who = actorName(form.decided_by)
  if (form.status === 'expired') {
    return <p className="text-sm text-muted-foreground">Expired {formatCalendarDate(form.expires_on) ?? ''}</p>
  }
  if (form.status === 'replaced') return <p className="text-sm text-muted-foreground">Replaced by a newer form</p>
  if (!form.decided_at) return null
  const verb = DECIDED[form.status]
  if (!verb) return null
  return (
    <p className="text-sm text-muted-foreground">
      {verb}
      {who ? ` by ${who}` : ''} · {formatDay(form.decided_at)}
    </p>
  )
}

/** The AI's call and what it read; deciding is the action item's job, so there are no buttons here. */
function DecisionPanel({ form }: { form: TaxExemptionDetail }) {
  const call = aiCall(form)
  const problems = form.ai_review?.problems ?? []
  return (
    <section aria-label="Decision" className="rounded-lg border bg-card">
      <div className="space-y-3 p-4">
        <p className={cn('flex items-center gap-1.5 text-sm font-medium', AI_TONES[call.tone])}>
          <Sparkles aria-hidden="true" className="size-4 shrink-0" />
          {call.text}
        </p>
        {form.ai_review?.staff_summary ? <p className="text-sm">{form.ai_review.staff_summary}</p> : null}
        {problems.length ? (
          <ul aria-label="Problems" className="grid gap-1 text-sm text-amber-900 dark:text-amber-200">
            {problems.map((problem, index) => (
              <li className="flex items-start gap-2" key={index}>
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                <span className="min-w-0 break-words">{problem}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <Decided form={form} />
      </div>
      <WhatTheAiRead form={form} />
      <Checks form={form} />
    </section>
  )
}

function TaxFreeOrders({ form }: { form: TaxExemptionDetail }) {
  const orders = form.tax_free_orders
  if (!orders.length) return null
  return (
    <section aria-labelledby="tax-free-orders" className="space-y-2">
      <h2 className="text-sm font-semibold" id="tax-free-orders">
        Orders placed tax-free
      </h2>
      <ul className="divide-y border-y text-sm">
        {orders.map((order) => (
          <li className="flex items-baseline justify-between gap-3 py-2" key={order.shopify_order_uuid}>
            <span className="min-w-0">
              <span className="font-medium">{order.name}</span>
              <span className="block text-xs text-muted-foreground">
                {formatDay(order.created_at)}
                {order.ship_to_state ? ` · ships to ${order.ship_to_state}` : ''} · {formatMoney(order.subtotal)}
              </span>
            </span>
            <span className="shrink-0 text-right tabular-nums">
              {order.estimated_tax != null ? (
                formatMoney(order.estimated_tax)
              ) : (
                <span className="text-xs text-muted-foreground">No tax rate</span>
              )}
            </span>
          </li>
        ))}
        <li className="flex items-baseline justify-between gap-3 py-2 font-medium">
          <span>Estimated tax</span>
          <span className="tabular-nums">{formatMoney(estimatedTaxTotal(orders))}</span>
        </li>
      </ul>
      {form.tax_invoice_ids.length ? (
        <p className="flex flex-wrap gap-x-3 text-sm">
          <span className="text-muted-foreground">Billed on</span>
          {form.tax_invoice_ids.map((invoiceId, index) => (
            <Link className="font-medium text-primary hover:underline" key={invoiceId} params={{ invoiceId }} to="/receivables/$invoiceId">
              {form.tax_invoice_ids.length > 1 ? `Tax invoice ${index + 1}` : 'Tax invoice'}
            </Link>
          ))}
        </p>
      ) : null}
    </section>
  )
}

function OtherForms({ form }: { form: TaxExemptionDetail }) {
  if (!form.other_forms.length) return null
  return (
    <section aria-labelledby="other-forms" className="space-y-2">
      <h2 className="text-sm font-semibold" id="other-forms">
        Their other forms
      </h2>
      <ul className="divide-y border-y text-sm">
        {form.other_forms.map((other) => (
          <li className="flex items-center justify-between gap-3 py-2" key={other.uuid}>
            <Link className="min-w-0 truncate font-medium hover:underline" params={{ formId: other.uuid }} to="/tax-exemptions/$formId">
              {other.holder_name || other.original_file_name}
              <span className="font-normal text-muted-foreground"> · Uploaded {formatDay(other.created_at)}</span>
            </Link>
            <StatusPill tone={STATUS[other.status].tone}>{STATUS[other.status].label}</StatusPill>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The form's story, behind one click: upload, the AI's call, the decision and what the customer was told. */
function History({ form }: { form: TaxExemptionDetail }) {
  const [open, setOpen] = useState(false)
  const actorName = useActorName()
  const entries: Array<{ when: string | null; text: ReactNode }> = [
    { when: form.created_at, text: `Uploaded ${SOURCE_LABELS[form.source]}` },
  ]
  if (form.ai_review) entries.push({ when: null, text: aiCall(form).text })
  if (form.shopify_exempt_set_at) entries.push({ when: form.shopify_exempt_set_at, text: 'Tax-exempt in Shopify' })
  if (form.decided_at) {
    const who = actorName(form.decided_by)
    entries.push({
      when: form.decided_at,
      text: (
        <>
          {STATUS[form.status].label}
          {who ? ` by ${who}` : ''}
          {form.staff_note ? <span className="block text-muted-foreground">Note: {form.staff_note}</span> : null}
          {form.customer_message ? (
            <span className="block whitespace-pre-line text-muted-foreground">Told the customer: {form.customer_message}</span>
          ) : null}
        </>
      ),
    })
  }
  if (form.shopify_exempt_removed_at) {
    entries.push({ when: form.shopify_exempt_removed_at, text: 'Exemption removed in Shopify' })
  }
  return (
    <section className="space-y-2">
      <Button
        aria-expanded={open}
        className="-ml-2 h-7 gap-1 px-2 text-sm font-semibold"
        onClick={() => setOpen(!open)}
        size="sm"
        variant="ghost"
      >
        History
        <ChevronDown aria-hidden="true" className={cn('size-3.5 transition-transform motion-reduce:transition-none', open && 'rotate-180')} />
      </Button>
      {open ? (
        <ol className="divide-y border-y text-sm">
          {entries.map((entry, index) => (
            <li className="grid gap-x-3 py-2 sm:grid-cols-[10rem_minmax(0,1fr)]" key={index}>
              <span className="text-xs tabular-nums text-muted-foreground sm:text-sm">{entry.when ? formatDayTime(entry.when) : ''}</span>
              <span className="min-w-0 break-words">{entry.text}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  )
}

/**
 * The form's action items, as on its customer thread's page (USA-1504): accounting's, owned by the
 * "Tax-exempt forms" person on Staff Defaults. Closed ones fold away; a form with none says so.
 */
function FormActions({ form, onEmailCustomer }: { form: TaxExemptionDetail; onEmailCustomer: (text: string) => void }) {
  const threadId = form.action_thread_id ?? 0
  const thread = useCustomerCommThread(threadId)
  const [showResolvedActions, setShowResolvedActions] = useState(false)
  const actions = thread.data?.actions.map(toPurchaseOrderAction) ?? []
  const comments = thread.data?.action_project_uuid ? { projectUuid: thread.data.action_project_uuid } : undefined
  // The review item's choices: Approve form and Reject form open their sheets from the card's
  // buttons; Email customer opens the reply box in the conversation below the form.
  const renderOption: ActionOptionRenderer = (option, trigger) => {
    if (option.option_type === 'email_customer') {
      return cloneElement(trigger as ReactElement<{ onClick?: () => void }>, {
        onClick: () => onEmailCustomer(option.reply_body ?? ''),
      })
    }
    if (!canDecide(form.status)) return undefined
    if (option.option_type === 'approve_tax_exemption') return <ApproveForm form={form} trigger={trigger} />
    if (option.option_type === 'decline_tax_exemption') return <RejectForm form={form} trigger={trigger} />
    return undefined
  }
  return (
    <ActionOptionContext.Provider value={renderOption}>
      <ActionItemSourceProvider value={{ kind: 'customer_thread', threadId }}>
        <section aria-labelledby="form-actions" className="space-y-4">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground" id="form-actions">
              Actions
            </h2>
            <p className="text-xs text-muted-foreground">
              Accounting ·{' '}
              {form.owner ? (
                <span className="font-medium text-foreground">{form.owner.name}</span>
              ) : (
                <Link className="hover:text-foreground hover:underline" to="/staff/defaults">
                  No owner set
                </Link>
              )}
            </p>
          </div>
          {threadId && thread.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <ActionItemsList
              comments={comments}
              customerEmail={thread.data?.recipient_email ?? form.customer_email}
              customerName={thread.data?.recipient_name ?? form.customer_name}
              lineItems={[]}
              openActions={actions.filter((action) => action.status === 'open')}
              poId=""
              reopens={thread.data?.action_reopens ?? []}
              resolvedActions={actions.filter((action) => action.status !== 'open')}
              setShowResolvedActions={setShowResolvedActions}
              shares={[]}
              showResolvedActions={showResolvedActions}
            />
          )}
        </section>
      </ActionItemSourceProvider>
    </ActionOptionContext.Provider>
  )
}

/**
 * The form's one conversation with the customer (USA-1504), on the same components as a customer
 * thread's page: every email we sent about the form, their replies, and staff's own replies and
 * questions, sent from the customer mailbox (status@).
 */
function FormConversation({ form, composeRequest }: { form: TaxExemptionDetail; composeRequest: ComposeRequest | null }) {
  const threadId = form.action_thread_id ?? 0
  const { data: thread, isLoading } = useCustomerCommThread(threadId)
  const approve = useApproveMessage()
  const reject = useRejectMessage()
  const send = useSendMessage()
  const update = useUpdateMessage()
  const record = useRecordCustomerComm(threadId)
  const failed = (title: string) => (error: unknown) => toast.error(formatApiErrorMessage(title, error))
  if (!threadId) return null
  if (isLoading || !thread) return <Skeleton className="h-48 w-full" />
  const actions = thread.actions.map(toPurchaseOrderAction)
  const comments = thread.action_project_uuid ? { projectUuid: thread.action_project_uuid } : undefined
  const greetingName = thread.recipient_name?.trim() || 'Customer'
  return (
    <div className="scroll-mt-4" id={CONVERSATION_ID}>
      <ActionEmailContext.Provider
        value={{
          renderAction: (item) => {
            const action = actions.find((candidate) => candidate.id === item.id)
            return action ? (
              <CustomerActionCard
                action={action}
                comments={comments}
                customerEmail={thread.recipient_email}
                customerName={thread.recipient_name}
                reopens={thread.action_reopens}
              />
            ) : null
          },
        }}
      >
        <POConversations
          actions={actions}
          contextForThread={() => ({
            greeting: `Dear ${greetingName},`,
            signoff: CUSTOMER_SIGNOFF,
            poDocuments: false,
            onRecord: (bodyText, source) =>
              record.mutate(
                { body_text: bodyText, message_type: 'note', source },
                { onSuccess: () => toast.success('Communication recorded'), onError: failed('Could not record it') },
              ),
            isRecording: record.isPending,
            recordPlaceholder: 'Summarize what the customer communicated...',
            composeRequest: composeRequest ?? undefined,
          })}
          emptyState={null}
          hasPoPdf={false}
          hasQuotePdf={false}
          isLoading={false}
          newThreadAction={null}
          onApprove={(uuid) => approve.mutate(uuid, { onError: failed('Not sent') })}
          onReject={(uuid) => reject.mutate(uuid, { onError: failed('Not rejected') })}
          onSend={(uuid) => send.mutate(uuid, { onError: failed('Not sent') })}
          onUpdate={(uuid, body) => update.mutate({ messageUuid: uuid, body }, { onError: failed('Draft not saved') })}
          shipmentDocOptions={[]}
          threads={[toConversationThread(thread)]}
          title="Emails with the customer"
        />
      </ActionEmailContext.Provider>
    </div>
  )
}

/**
 * The status pill. An approved form can be rejected from it (and a declined one approved): picking
 * opens the same sheet the action item does, and the customer is emailed either way.
 */
function StatusControl({ form }: { form: TaxExemptionDetail }) {
  const [sheet, setSheet] = useState<{ kind: 'approve' | 'reject'; key: number } | null>(null)
  const status = STATUS[form.status]
  const pill = <StatusPill tone={status.tone}>{status.label}</StatusPill>
  const choices: PickerChoice[] =
    form.status === 'approved'
      ? [
          { value: 'approved', label: STATUS.approved.label, tone: STATUS.approved.tone },
          { value: 'revoked', label: STATUS.revoked.label, tone: STATUS.revoked.tone },
        ]
      : form.status === 'declined'
        ? [
            { value: 'declined', label: STATUS.declined.label, tone: STATUS.declined.tone },
            { value: 'approved', label: STATUS.approved.label, tone: STATUS.approved.tone },
          ]
        : []
  if (!choices.length) return pill
  const close = (open: boolean) => {
    if (!open) setSheet(null)
  }
  const anchor = <span aria-hidden="true" className="inline-block h-6 w-0" />
  return (
    <span className="inline-flex items-center">
      <ChoicePicker
        choices={choices}
        label={`Status: ${status.label}. Change status`}
        onSelect={(next) => setSheet({ kind: next === 'approved' ? 'approve' : 'reject', key: Date.now() })}
        opensFollowUp
        title="Form status"
        value={form.status}
      >
        {pill}
      </ChoicePicker>
      {form.status === 'approved' ? (
        <RejectForm anchorOnly form={form} key={sheet?.key} onOpenChange={close} open={sheet?.kind === 'reject'} trigger={anchor} />
      ) : (
        <ApproveForm anchorOnly form={form} key={sheet?.key} onOpenChange={close} open={sheet?.kind === 'approve'} trigger={anchor} />
      )}
    </span>
  )
}

/** The customer's name, linked to their customer page when they are one of ours. */
function CustomerName({ form, name }: { form: TaxExemptionDetail; name: string }) {
  if (!form.customer_group_uuid) return <>{name}</>
  return (
    <Link
      className="underline decoration-muted-foreground/40 underline-offset-4 hover:decoration-foreground"
      params={{ groupId: form.customer_group_uuid }}
      to="/customers/$groupId"
    >
      {name}
    </Link>
  )
}

function DetailSkeleton() {
  return (
    <div aria-label="Loading" className="mx-auto max-w-7xl space-y-4" role="status">
      <Skeleton className="h-8 w-80 max-w-full" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <Skeleton className="h-[60vh]" />
        <div className="space-y-6">
          <Skeleton className="h-40" />
          <Skeleton className="h-80" />
        </div>
      </div>
    </div>
  )
}

/** One customer's tax-exemption form (USA-1504), in the customer mailbox like a customer thread's page. */
export function TaxExemptionDetailPage({ formId }: { formId: string }) {
  return (
    <ThreadMailboxProvider value="customer">
      <FormPage formId={formId} />
    </ThreadMailboxProvider>
  )
}

/**
 * The form and its conversation on the left; on the right its action item (the only place it is
 * decided) above what the AI read. On a phone: the item, the form, what the AI read, the emails.
 */
function FormPage({ formId }: { formId: string }) {
  const query = useTaxExemption(formId)
  const [composeRequest, setComposeRequest] = useState<ComposeRequest | null>(null)
  const form = query.data
  const name = form ? form.customer_name || form.customer_email || 'Store customer' : null
  usePageTitle(name && `Tax exemption · ${name}`)

  if (query.isLoading) return <DetailSkeleton />
  if (!form) {
    return (
      <div className="mx-auto max-w-7xl py-12 text-center text-sm text-muted-foreground">
        <p>This form could not be loaded.</p>
        <Button className="mt-3" onClick={() => void query.refetch()} size="sm" variant="outline">
          Try again
        </Button>
      </div>
    )
  }
  const emailCustomer = (text: string) => {
    setComposeRequest({ id: Date.now(), text })
    requestAnimationFrame(() =>
      document.getElementById(CONVERSATION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    )
  }

  return (
    <div className="mx-auto min-w-0 max-w-7xl pb-24">
      <nav className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        <BackButton fallback={{ to: '/tax-exemptions' }} fallbackLabel="Back to Tax exemptions" iconOnly />
        <Link className="hover:text-foreground hover:underline" to="/tax-exemptions">
          Tax exemptions
        </Link>
      </nav>
      <div className="mb-5 mt-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <h1 className="break-words text-xl font-bold tracking-tight md:text-2xl">
            <CustomerName form={form} name={name ?? 'Store customer'} />
          </h1>
          <StatusControl form={form} />
        </div>
        <p className="mt-1 flex flex-wrap gap-x-2 text-sm text-muted-foreground">
          {form.customer_name && form.customer_email ? (
            <>
              <span>{form.customer_email}</span>
              <span aria-hidden="true">·</span>
            </>
          ) : null}
          <span>
            Uploaded {formatDay(form.created_at)} {SOURCE_LABELS[form.source]}
          </span>
        </p>
      </div>

      {/* Phones stack the columns' pieces in reading order (`contents` + `order`); wide screens keep two columns. */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-start">
        <div className="contents lg:col-start-1 lg:row-start-1 lg:block lg:min-w-0 lg:space-y-8">
          <div className="order-2 min-w-0">
            <CertificatePreview form={form} />
          </div>
          <div className="order-4 min-w-0">
            <FormConversation composeRequest={composeRequest} form={form} />
          </div>
          <div className="order-5 min-w-0 space-y-6">
            <TaxFreeOrders form={form} />
            <OtherForms form={form} />
            <History form={form} />
          </div>
        </div>
        <div className="contents lg:col-start-2 lg:row-start-1 lg:block lg:min-w-0 lg:space-y-6">
          <div className="order-1 min-w-0">
            <FormActions form={form} onEmailCustomer={emailCustomer} />
          </div>
          <div className="order-3 min-w-0">
            <DecisionPanel form={form} />
          </div>
        </div>
      </div>
    </div>
  )
}
